import { useEffect, useState } from 'react';
import {
  METHODS,
  METHOD_LABELS,
  PROVENANCES,
  PROVENANCE_LABELS,
  SPLIT_CRITERIA,
  SPLIT_CRITERION_LABELS,
  UNIT_TYPES,
  UNIT_TYPE_LABELS,
  type Entity,
  type Method,
  type Provenance,
  type SplitCriterion,
  type UnitType,
  type ValuationUnit,
} from '@ave/shared';
import { api, type EngagementDetail } from '../lib/api';
import { longDate, money } from '../lib/format';
import { Badge, Card, EmptyState, ProvenanceChip, SectionHeading } from './ui';
import { Icon } from './icons';

type ShareTransaction = EngagementDetail['shareTransactions'][number];

const INPUT =
  'mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
const SELECT =
  'mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

/**
 * The twelve-month window Stage 4 tests, computed the way Postgres computes
 * it: `date - INTERVAL '12 months'` clamps 29 February to the 28th rather
 * than rolling into March.
 *
 * This is only deciding whether to *ask* the anchor question — the gate does
 * its own arithmetic in SQL and remains the authority. Matching it here means
 * the form asks in exactly the cases the gate will later demand an answer.
 */
function twelveMonthsBefore(isoDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return isoDate;
  const year = Number(isoDate.slice(0, 4)) - 1;
  const monthDay = isoDate.slice(5);
  return `${year}-${monthDay === '02-29' ? '02-28' : monthDay}`;
}

/**
 * Valuation units, and everything a preparer does to one before it can be
 * calculated.
 *
 * The unit is the atomic object of the engine (§5.2) — every methodology,
 * calculation and value hangs off one, never off an entity. Until this
 * existed a unit could only be created by calling the API by hand, so Stage 3
 * had exactly the problem Stage 0 had before the holding form: the workspace
 * could show that a unit was missing and offer no way to supply it.
 *
 * Three things the card does rather than merely display.
 *
 * **It asks for the split criterion at the moment the split is made.** A unit
 * that is not the whole company only exists if the carve changes the basis,
 * the risk multiple, or standalone saleability (§5.2/§8) — enforced by the
 * API, a CHECK constraint, and a Stage 3 gate. Asking afterwards means asking
 * someone to justify a decision they have stopped thinking about.
 *
 * **It asks the anchor-or-justify question where the engagement has a recent
 * arm's-length transaction.** That is a Stage 4 blocking check, and a
 * methodology saved without an answer blocks its own gate the moment it is
 * written. The transaction is on screen while the question is answered, which
 * is the only time the answer is cheap to give.
 *
 * **It keeps proposal and approval apart.** Proposing is a preparer's act and
 * approving is a reviewer's; re-proposing clears the approval, so the button
 * on a unit that already has an approved methodology says what that will
 * cost.
 */
export function ValuationUnits({
  engagementId,
  valuationDate,
  units,
  entities,
  shareTransactions,
  canWrite,
  canReview,
  onChange,
  openSignal = 0,
}: {
  engagementId: string;
  valuationDate: string;
  units: ValuationUnit[];
  entities: Entity[];
  shareTransactions: ShareTransaction[];
  canWrite: boolean;
  canReview: boolean;
  onChange: () => void | Promise<void>;
  /** Increments when a blocking check asks for the unit form. */
  openSignal?: number;
}) {
  // The transactions that force anchor-or-justify: arm's length, in the
  // subject shares, within twelve months to the valuation date.
  const cutoff = twelveMonthsBefore(valuationDate);
  const anchorCandidates = shareTransactions.filter(
    (t) =>
      t.counterpartyRelationship === 'arms_length' &&
      t.txnDate <= valuationDate &&
      t.txnDate >= cutoff,
  );

  return (
    <Card>
      <SectionHeading
        title="Valuation units & methodology"
        subtitle="The atomic object of the engine: every calculation and every value attaches to a unit"
      />

      {units.length === 0 ? (
        <EmptyState
          title="No valuation unit defined"
          detail="Stage 3 does not close without one, and nothing can be calculated until a unit exists to calculate. Most engagements need exactly one — the whole company."
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {units.map((unit) => (
            <li key={unit.id} data-item-id={unit.id} className="scroll-mt-24">
              <UnitRow
                unit={unit}
                entities={entities}
                anchorCandidates={anchorCandidates}
                canWrite={canWrite}
                canReview={canReview}
                onChange={onChange}
              />
            </li>
          ))}
        </ul>
      )}

      {canWrite ? (
        <DefineUnit
          engagementId={engagementId}
          entities={entities}
          existingUnits={units.length}
          onSaved={onChange}
          openSignal={openSignal}
        />
      ) : null}
    </Card>
  );
}

function UnitRow({
  unit,
  entities,
  anchorCandidates,
  canWrite,
  canReview,
  onChange,
}: {
  unit: ValuationUnit;
  entities: Entity[];
  anchorCandidates: ShareTransaction[];
  canWrite: boolean;
  canReview: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entity = entities.find((e) => e.id === unit.entityId);
  const m = unit.methodology ?? null;

  const approve = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.approveMethodology(unit.id);
      await onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not approve the methodology');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg px-6 py-4">
      <p className="text-sm font-medium text-slate-900">{unit.name}</p>
      {entity ? <p className="mt-0.5 text-xs text-slate-400">in {entity.name}</p> : null}
      {unit.activityDescription ? (
        <p className="mt-1 text-sm leading-relaxed text-slate-500">{unit.activityDescription}</p>
      ) : null}

      <div className="mt-2 flex flex-wrap gap-1.5">
        <Badge tone="slate">{UNIT_TYPE_LABELS[unit.unitType]}</Badge>
        <ProvenanceChip provenance={unit.provenance} />
        {unit.standaloneSaleable ? <Badge tone="slate">standalone saleable</Badge> : null}
        {m ? (
          <Badge tone={m.approvedBy ? 'emerald' : 'amber'}>
            {METHOD_LABELS[m.method]}
            {m.approvedBy ? ' · approved' : ' · awaiting approval'}
          </Badge>
        ) : (
          <Badge tone="amber">No methodology</Badge>
        )}
      </div>

      {/* Why this unit is allowed to exist as a separate thing. Shown rather
          than folded away: it is what a reviewer asks about a build-up with
          more than one unit in it. */}
      {unit.splitCriterion ? (
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          <span className="text-slate-400">Split because: </span>
          {SPLIT_CRITERION_LABELS[unit.splitCriterion]}
          {unit.splitRationale ? ` — ${unit.splitRationale}` : ''}
        </p>
      ) : null}

      {/* The methodology carries its own item id: the Stage 4 checks point at
          the methodology row, not the unit, so a blocking check that says
          "approve the methodology for this unit" has something to scroll to. */}
      {m ? (
        <div data-item-id={m.id} className="mt-2 scroll-mt-24">
          <p className="text-sm leading-relaxed text-slate-600">{m.rationale}</p>
          {m.anchorTransactionId ? (
            <p className="mt-1 text-xs text-slate-500">
              Anchored to a recorded transaction in the subject shares.
            </p>
          ) : m.anchorDeclinedReason ? (
            <p className="mt-1 text-xs leading-relaxed text-slate-500">
              <span className="text-slate-400">Not anchored: </span>
              {m.anchorDeclinedReason}
            </p>
          ) : anchorCandidates.length > 0 ? (
            <p className="mt-1 text-xs leading-relaxed text-amber-700">
              A recent arm’s-length transaction exists and this methodology neither anchors to it
              nor says why not. Stage 4 will not pass.
            </p>
          ) : null}
          <p className="mt-1 text-xs text-slate-400">
            Proposed by {m.proposedBy === 'human' ? 'a preparer' : m.proposedBy}
            {m.approvedAt ? ` · approved ${longDate(m.approvedAt)}` : ''}
          </p>
        </div>
      ) : null}

      {error ? <p className="mt-2 text-sm text-rose-600">{error}</p> : null}

      {!editing && !correcting ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canWrite ? (
            <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
              {m ? 'Change the methodology' : 'Select a methodology'}
            </button>
          ) : null}
          {/* Approval is a reviewer act and Stage 4 will not exit without it,
              so the button is offered the moment there is something to
              approve rather than left to be found on another screen. */}
          {m && !m.approvedBy && canWrite && canReview ? (
            <button type="button" className="btn-primary" onClick={approve} disabled={busy}>
              {busy ? 'Approving…' : 'Approve the methodology'}
            </button>
          ) : null}
          {/* Quieter than the others on purpose: correcting a unit is the
              rare act, and the common one is choosing how to value it. */}
          {canWrite ? (
            <button
              type="button"
              className="text-xs font-medium text-slate-400 hover:text-slate-700"
              onClick={() => setCorrecting(true)}
            >
              Correct this unit
            </button>
          ) : null}
        </div>
      ) : null}

      {editing ? (
        <MethodologyForm
          unit={unit}
          anchorCandidates={anchorCandidates}
          onClose={() => setEditing(false)}
          onSaved={onChange}
        />
      ) : null}

      {correcting ? (
        <CorrectUnitForm
          unit={unit}
          approved={Boolean(m?.approvedBy)}
          onClose={() => setCorrecting(false)}
          onSaved={async () => {
            setCorrecting(false);
            await onChange();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Fixing a unit that was named wrongly or defined in error.
 *
 * Two different edits sit behind one button and the form keeps them visibly
 * apart. Renaming is cosmetic. Changing what the unit *is* — its type, its
 * split criterion, whether it stands alone — changes the thing a reviewer
 * approved a methodology for, so it withdraws that approval, and the form
 * says so while it can still be reconsidered rather than reporting it after.
 *
 * Removal is refused by the API where anything has been calculated against
 * the unit, and the reason arrives in those words: the working would go with
 * it, and a calculation that has disappeared is what the audit trail exists
 * to prevent.
 */
function CorrectUnitForm({
  unit,
  approved,
  onClose,
  onSaved,
}: {
  unit: ValuationUnit;
  approved: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [name, setName] = useState(unit.name);
  const [activity, setActivity] = useState(unit.activityDescription ?? '');
  const [unitType, setUnitType] = useState<UnitType>(unit.unitType);
  const [splitCriterion, setSplitCriterion] = useState<SplitCriterion | ''>(
    unit.splitCriterion ?? '',
  );
  const [splitRationale, setSplitRationale] = useState(unit.splitRationale ?? '');
  const [standalone, setStandalone] = useState(unit.standaloneSaleable);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redefining =
    unitType !== unit.unitType ||
    (splitCriterion || null) !== (unit.splitCriterion ?? null) ||
    standalone !== unit.standaloneSaleable;
  const needsCriterion = unitType !== 'whole_entity' && splitCriterion === '';
  const valid = name.trim() !== '' && !needsCriterion;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          Name
          <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="text-sm text-slate-600">
          What it is
          <select
            className={SELECT}
            value={unitType}
            onChange={(e) => setUnitType(e.target.value as UnitType)}
          >
            {UNIT_TYPES.map((t) => (
              <option key={t} value={t}>
                {UNIT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="mt-3 block text-sm text-slate-600">
        What it does
        <input
          className={INPUT}
          value={activity}
          placeholder="Optional"
          onChange={(e) => setActivity(e.target.value)}
        />
      </label>

      {unitType !== 'whole_entity' ? (
        <>
          <label className="mt-3 block text-sm text-slate-600">
            Why this is a separate unit
            <select
              className={SELECT}
              value={splitCriterion}
              onChange={(e) => setSplitCriterion(e.target.value as SplitCriterion | '')}
            >
              <option value="">Choose the criterion</option>
              {SPLIT_CRITERIA.map((c) => (
                <option key={c} value={c}>
                  {SPLIT_CRITERION_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
          <label className="mt-3 block text-sm text-slate-600">
            How it applies here
            <input
              className={INPUT}
              value={splitRationale}
              onChange={(e) => setSplitRationale(e.target.value)}
            />
          </label>
        </>
      ) : null}

      <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={standalone}
          onChange={(e) => setStandalone(e.target.checked)}
        />
        Could be sold on its own
      </label>

      {redefining && approved ? (
        <p className="mt-3 text-sm leading-relaxed text-amber-700">
          This changes what the unit is rather than what it is called, so the approved methodology
          stops being approved. A reviewer approved a method for the unit as it was described, and
          they have not seen this one.
        </p>
      ) : null}

      {error ? <p className="mt-3 text-sm text-rose-600">{error}</p> : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={!valid || busy}
          onClick={() =>
            run(() =>
              api.updateValuationUnit(unit.id, {
                name: name.trim(),
                activityDescription: activity.trim() || null,
                unitType,
                standaloneSaleable: standalone,
                splitCriterion: unitType === 'whole_entity' ? null : splitCriterion,
                splitRationale: splitRationale.trim() || null,
              }),
            )
          }
        >
          Save the correction
        </button>
        <button
          type="button"
          className="ml-auto text-xs font-medium text-slate-400 hover:text-rose-600"
          disabled={busy}
          onClick={() => run(() => api.removeValuationUnit(unit.id))}
        >
          Remove this unit
        </button>
      </div>
    </div>
  );
}

function MethodologyForm({
  unit,
  anchorCandidates,
  onClose,
  onSaved,
}: {
  unit: ValuationUnit;
  anchorCandidates: ShareTransaction[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const existing = unit.methodology ?? null;
  const [method, setMethod] = useState<Method>(existing?.method ?? 'earnings_multiple');
  const [rationale, setRationale] = useState(existing?.rationale ?? '');
  const [anchorTransactionId, setAnchorTransactionId] = useState<string>(
    existing?.anchorTransactionId ?? '',
  );
  const [anchorDeclinedReason, setAnchorDeclinedReason] = useState(
    existing?.anchorDeclinedReason ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mustAnswerAnchor = anchorCandidates.length > 0;
  const anchored = anchorTransactionId !== '';
  // The rationale minimum is the API's own (min 10). Enforced here so the
  // refusal reads as a form telling you what it needs rather than a 400.
  const valid =
    rationale.trim().length >= 10 &&
    (!mustAnswerAnchor || anchored || anchorDeclinedReason.trim().length >= 10);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.setMethodology(unit.id, {
        method,
        rationale: rationale.trim(),
        anchorTransactionId: anchored ? anchorTransactionId : null,
        // A reason is meaningless alongside an anchor, and would sit in the
        // defence file contradicting it.
        anchorDeclinedReason: anchored ? undefined : anchorDeclinedReason.trim() || undefined,
        proposedBy: 'human',
      });
      onClose();
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the methodology');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-4 rounded-lg bg-slate-50/80 p-4">
      {existing?.approvedBy ? (
        <p className="text-sm leading-relaxed text-amber-700">
          This methodology is approved. Changing it withdraws the approval, and a reviewer will
          have to approve the replacement before Stage 4 can pass again.
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="label">Method</span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as Method)}
            className={SELECT}
          >
            {METHODS.map((k) => (
              <option key={k} value={k}>
                {METHOD_LABELS[k]}
              </option>
            ))}
          </select>
          {method === 'opm_waterfall' ? (
            <span className="mt-1 block text-xs text-amber-700">
              The OPM waterfall is a Phase 2 model and the engine will not price it yet.
            </span>
          ) : null}
        </label>

        <label className="block sm:col-span-2">
          <span className="label">Why this method, for this unit</span>
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            placeholder="e.g. The company's value is in its property portfolio rather than its trading profits, which are marginal and volatile. Net asset value is the basis a purchaser would use, and the one the comparables in this sector are struck on."
            className={INPUT}
          />
          <span className="mt-1 block text-xs text-slate-400">
            Goes into the report as the reason the number was arrived at this way, and into the
            defence file. Ten characters is the minimum the API accepts; a sentence is the minimum
            a reviewer will.
          </span>
        </label>

        {/* Anchor-or-justify (§7 stage 4). Only asked where a recent
            arm's-length transaction actually exists — a form that asked
            always would train people to skip it. */}
        {mustAnswerAnchor ? (
          <div className="space-y-3 sm:col-span-2">
            <label className="block">
              <span className="label">
                Anchor to a recent transaction
                <span className="ml-1.5 font-normal normal-case tracking-normal text-amber-700">
                  required — one exists
                </span>
              </span>
              <select
                value={anchorTransactionId}
                onChange={(e) => setAnchorTransactionId(e.target.value)}
                className={SELECT}
              >
                <option value="">Not anchored to any of them</option>
                {anchorCandidates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {longDate(t.txnDate)} — {money(t.pricePerShare)} / share, {t.shareClass}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-slate-400">
                A recent arm’s-length transaction in the subject shares is near-determinative, so
                every methodology must either anchor to it or record why it was rejected.
              </span>
            </label>

            {!anchored ? (
              <label className="block">
                <span className="label">Why not</span>
                <textarea
                  value={anchorDeclinedReason}
                  onChange={(e) => setAnchorDeclinedReason(e.target.value)}
                  rows={2}
                  placeholder="e.g. The allotment was to an incoming director at a price set by the subscription agreement rather than negotiated between unconnected parties, so it is not evidence of open-market value."
                  className={INPUT}
                />
                <span className="mt-1 block text-xs text-slate-400">
                  This is the answer to the question an HMRC officer asks first.
                </span>
              </label>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? <p className="text-sm text-rose-600">{error}</p> : null}

      <div className="flex items-center justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Recording…' : existing ? 'Replace the methodology' : 'Record the methodology'}
        </button>
      </div>
    </div>
  );
}

/**
 * Defining a unit, and the entity it sits in.
 *
 * The default is the whole company, because that is what most engagements
 * have and because a split has to be earned. Choosing anything else opens the
 * criterion question rather than letting the API refuse the save afterwards.
 */
function DefineUnit({
  engagementId,
  entities,
  existingUnits,
  onSaved,
  openSignal,
}: {
  engagementId: string;
  entities: Entity[];
  existingUnits: number;
  onSaved: () => void | Promise<void>;
  openSignal: number;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (openSignal > 0) setOpen(true);
  }, [openSignal]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [entityId, setEntityId] = useState<string>(entities[0]?.id ?? 'new');
  const [entityName, setEntityName] = useState('');
  const [companiesHouseNo, setCompaniesHouseNo] = useState('');

  const [name, setName] = useState('');
  const [unitType, setUnitType] = useState<UnitType>('whole_entity');
  const [activityDescription, setActivityDescription] = useState('');
  const [provenance, setProvenance] = useState<Provenance>('data_derived');
  const [splitCriterion, setSplitCriterion] = useState<SplitCriterion>('basis_change');
  const [splitRationale, setSplitRationale] = useState('');
  const [standaloneSaleable, setStandaloneSaleable] = useState(false);
  const [disaggregatable, setDisaggregatable] = useState(false);

  const creatingEntity = entityId === 'new';
  const isSplit = unitType !== 'whole_entity';

  // The API refuses a split with no criterion (422) and a CHECK constraint
  // refuses it underneath. The form asks for the rationale too, which neither
  // of those requires: "basis_change" on its own is a category, not a reason.
  const valid =
    name.trim().length > 1 &&
    (creatingEntity ? entityName.trim().length > 1 : true) &&
    (!isSplit || splitRationale.trim().length >= 10);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const id = creatingEntity
        ? (
            await api.createEntity(engagementId, {
              name: entityName.trim(),
              companiesHouseNo: companiesHouseNo.trim() || undefined,
              entityType: 'company',
            })
          ).entity.id
        : entityId;

      await api.createValuationUnit(engagementId, {
        entityId: id,
        name: name.trim(),
        unitType,
        activityDescription: activityDescription.trim() || undefined,
        provenance,
        disaggregatable,
        standaloneSaleable,
        splitCriterion: isSplit ? splitCriterion : null,
        splitRationale: isSplit ? splitRationale.trim() : undefined,
      });

      setName('');
      setActivityDescription('');
      setSplitRationale('');
      setUnitType('whole_entity');
      setStandaloneSaleable(false);
      setDisaggregatable(false);
      setEntityName('');
      setCompaniesHouseNo('');
      setOpen(false);
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not define the unit');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="border-t border-slate-100 px-6 py-3">
        <button type="button" className="btn-secondary" onClick={() => setOpen(true)}>
          <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
          Define a valuation unit
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 border-t border-slate-100 bg-slate-50/60 px-6 py-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="label">The company this unit is part of</span>
          <select
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className={SELECT}
          >
            {entities.map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
                {entity.companiesHouseNo ? ` (${entity.companiesHouseNo})` : ''}
              </option>
            ))}
            <option value="new">A company not yet recorded…</option>
          </select>
        </label>

        {creatingEntity ? (
          <>
            <label className="block">
              <span className="label">Company name</span>
              <input
                autoFocus
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
                placeholder="e.g. Marchpole Estates Limited"
                className={INPUT}
              />
            </label>
            <label className="block">
              <span className="label">Companies House number</span>
              <input
                value={companiesHouseNo}
                onChange={(e) => setCompaniesHouseNo(e.target.value)}
                placeholder="Optional, but it is what the register is pulled on"
                className={INPUT}
              />
            </label>
          </>
        ) : null}

        <label className="block">
          <span className="label">What this unit is</span>
          <select
            value={unitType}
            onChange={(e) => setUnitType(e.target.value as UnitType)}
            className={SELECT}
          >
            {UNIT_TYPES.map((k) => (
              <option key={k} value={k}>
                {UNIT_TYPE_LABELS[k]}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-slate-400">
            {isSplit
              ? 'A split unit values one piece of the business separately, and has to be justified.'
              : existingUnits === 0
                ? 'The ordinary case. Most engagements are one unit — the whole company.'
                : 'A second whole-company unit is unusual; a split is more likely what you want.'}
          </span>
        </label>

        <label className="block">
          <span className="label">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={isSplit ? 'e.g. Northern haulage division' : 'e.g. Marchpole Estates Limited — whole company'}
            className={INPUT}
          />
        </label>

        <label className="block sm:col-span-2">
          <span className="label">What it does</span>
          <textarea
            value={activityDescription}
            onChange={(e) => setActivityDescription(e.target.value)}
            rows={2}
            placeholder="e.g. Holds and lets the freehold commercial portfolio; no trading activity."
            className={INPUT}
          />
        </label>

        {isSplit ? (
          <>
            <label className="block sm:col-span-2">
              <span className="label">
                Which criterion justifies the split
                <span className="ml-1.5 font-normal normal-case tracking-normal text-amber-700">
                  required — §5.2
                </span>
              </span>
              <select
                value={splitCriterion}
                onChange={(e) => setSplitCriterion(e.target.value as SplitCriterion)}
                className={SELECT}
              >
                {SPLIT_CRITERIA.map((k) => (
                  <option key={k} value={k}>
                    {SPLIT_CRITERION_LABELS[k]}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-slate-400">
                A split is only permitted where it changes the basis, the risk profile and so the
                multiple, or standalone saleability. Anything else is commentary about the
                business rather than a unit of it.
              </span>
            </label>

            <label className="block sm:col-span-2">
              <span className="label">How it applies here</span>
              <textarea
                value={splitRationale}
                onChange={(e) => setSplitRationale(e.target.value)}
                rows={2}
                placeholder="e.g. The haulage division has its own depots, contracts and licence, and was the subject of an approach in 2025. It would sell without the property arm."
                className={INPUT}
              />
              <span className="mt-1 block text-xs text-slate-400">
                The criterion is the category; this is the reason. A reviewer reads this one.
              </span>
            </label>
          </>
        ) : null}

        <label className="block">
          <span className="label">Provenance</span>
          <select
            value={provenance}
            onChange={(e) => setProvenance(e.target.value as Provenance)}
            className={SELECT}
          >
            {PROVENANCES.map((p) => (
              <option key={p} value={p}>
                {PROVENANCE_LABELS[p]}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-xs text-slate-400">
            Where the shape of this unit came from — the filed accounts, or what management told
            us. The report says which.
          </span>
        </label>

        <div className="space-y-2 self-end pb-1">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={standaloneSaleable}
              onChange={(e) => setStandaloneSaleable(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            Could be sold on its own
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={disaggregatable}
              onChange={(e) => setDisaggregatable(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            Could be split further
          </label>
        </div>
      </div>

      {error ? <p className="text-sm text-rose-600">{error}</p> : null}

      <div className="flex items-center justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={() => setOpen(false)} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={!valid || busy}>
          {busy ? 'Defining…' : 'Define the unit'}
        </button>
      </div>
    </div>
  );
}
